import React, { useState, useEffect, useRef } from 'react';
import { Layout } from '../components/Layout';
import { supabase } from '../lib/supabase';
import { Search, User, Check, X, Loader2 } from 'lucide-react';
import QRCode from 'qrcode';
import { PatientQRCodePDF } from '../components/patient-registration/PatientQRCodePDF';

interface PatientData {
  id: string;
  patient_id: string;
  first_name: string;
  last_name: string;
  date_of_birth: string;
}

interface SearchResult {
  id: string;
  patient_id: string;
  first_name: string;
  last_name: string;
  date_of_birth: string;
}

export function RegisterPatientApp() {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [selectedPatient, setSelectedPatient] = useState<PatientData | null>(null);
  const [isRegistered, setIsRegistered] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string | null>(null);
  const [genericQrCodeDataUrl, setGenericQrCodeDataUrl] = useState<string | null>(null);
  const [showResults, setShowResults] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  // Generate generic QR code on component mount
  useEffect(() => {
    generateGenericQRCode();
  }, []);

  // Generate QR code when patient is selected
  useEffect(() => {
    if (selectedPatient) {
      generateQRCode();
      checkRegistrationStatus();
    } else {
      setQrCodeDataUrl(null);
    }
  }, [selectedPatient]);

  // Handle click outside search results
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        resultsRef.current && 
        !resultsRef.current.contains(event.target as Node) &&
        searchInputRef.current && 
        !searchInputRef.current.contains(event.target as Node)
      ) {
        setShowResults(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Search for patients as user types
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchTerm.length >= 3) {
        searchPatients();
        setShowResults(true);
      } else {
        setSearchResults([]);
        setShowResults(false);
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  const searchPatients = async () => {
    if (!searchTerm.trim()) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('patients')
        .select('id, patient_id, first_name, last_name, date_of_birth')
        .or(`first_name.ilike.%${searchTerm}%,last_name.ilike.%${searchTerm}%,patient_id.ilike.%${searchTerm}%`)
        .not('deleted', 'eq', true)
        .limit(10);

      if (error) throw error;
      setSearchResults(data || []);
    } catch (error) {
      console.error('Error searching patients:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePatientSelect = (patient: SearchResult) => {
    setSelectedPatient(patient);
    setSearchTerm(`${patient.first_name} ${patient.last_name} (${patient.patient_id})`);
    setShowResults(false);
  };

  const checkRegistrationStatus = async () => {
    if (!selectedPatient) return;
    
    setCheckingStatus(true);
    try {
      // Check if patient has a profile record with patient_id matching the selected patient
      const { data, error } = await supabase
        .from('profiles')
        .select('patient_id')
        .eq('patient_id', selectedPatient.patient_id)
        .limit(1);
      console.log('checking profile table for patient_id:',selectedPatient.patient_id );
      console.log ('data.length = ', data.length);
      if (error) throw error;
      setIsRegistered(data && data.length > 0);
     // console.log (isRegistered);
    } catch (error) {
      console.error('Error checking registration status:', error);
    } finally {
      setCheckingStatus(false);
    }
  };

  const generateQRCode = async () => {
    if (!selectedPatient) return;
    
    try {
      // Create a registration URL with the patient ID
      const registrationUrl = `https://mobileapp.thrivemhw.com/register?patientId=${selectedPatient.id}`;
      
      // Generate QR code
      const dataUrl = await QRCode.toDataURL(registrationUrl, {
        width: 200,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#ffffff'
        }
      });
      
      setQrCodeDataUrl(dataUrl);
    } catch (error) {
      console.error('Error generating QR code:', error);
    }
  };

  const generateGenericQRCode = async () => {
    try {
      // Create a generic URL to the homepage
      const genericUrl = 'https://mobileapp.thrivemhw.com/';
      
      // Generate QR code
      const dataUrl = await QRCode.toDataURL(genericUrl, {
        width: 200,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#ffffff'
        }
      });
      
      setGenericQrCodeDataUrl(dataUrl);
    } catch (error) {
      console.error('Error generating generic QR code:', error);
    }
  };

  const clearSelection = () => {
    setSelectedPatient(null);
    setSearchTerm('');
    setIsRegistered(null);
    setQrCodeDataUrl(null);
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  return (
    <Layout title="Register Patient for Mobile App">
      <div className="bg-white shadow-md rounded-lg p-6 max-w-4xl mx-auto">
        <div className="mb-8">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Search for a Patient</h2>
          <div className="relative">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-green-500 focus:border-green-500"
                placeholder="Search by name or patient ID..."
              />
              {searchTerm && (
                <button
                  onClick={clearSelection}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  <X className="h-5 w-5 text-gray-400 hover:text-gray-500" />
                </button>
              )}
            </div>
            
            {/* Search Results Dropdown */}
            {showResults && searchResults.length > 0 && (
              <div 
                ref={resultsRef}
                className="absolute z-10 mt-1 w-full bg-white shadow-lg rounded-md overflow-hidden border border-gray-200"
              >
                <ul className="max-h-60 overflow-y-auto py-1">
                  {searchResults.map((patient) => (
                    <li 
                      key={patient.id}
                      onClick={() => handlePatientSelect(patient)}
                      className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                    >
                      <div className="flex items-center">
                        <User className="h-5 w-5 text-gray-400 mr-2" />
                        <div>
                          <div className="font-medium text-gray-900">
                            {patient.first_name} {patient.last_name}
                          </div>
                          <div className="text-sm text-gray-500">
                            ID: {patient.patient_id}
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {showResults && searchTerm.length >= 3 && searchResults.length === 0 && !loading && (
              <div 
                ref={resultsRef}
                className="absolute z-10 mt-1 w-full bg-white shadow-lg rounded-md overflow-hidden border border-gray-200"
              >
                <div className="px-4 py-3 text-sm text-gray-500">
                  No patients found matching "{searchTerm}"
                </div>
              </div>
            )}
          </div>
          
          {loading && (
            <div className="mt-2 flex items-center text-sm text-gray-500">
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Searching...
            </div>
          )}
        </div>

        {selectedPatient && (
          <div className="mb-8 p-4 bg-gray-50 rounded-lg">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Selected Patient</h3>
            <div className="flex items-center">
              <User className="h-5 w-5 text-gray-400 mr-2" />
              <div>
                <div className="font-medium text-gray-900">
                  {selectedPatient.first_name} {selectedPatient.last_name}
                </div>
                <div className="text-sm text-gray-500">
                  ID: {selectedPatient.patient_id}
                </div>
              </div>
            </div>
            
            {checkingStatus ? (
              <div className="mt-3 flex items-center text-sm text-gray-500">
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Checking registration status...
              </div>
            ) : isRegistered !== null && (
              <div className={`mt-3 p-3 rounded-md ${isRegistered ? 'bg-green-50' : 'bg-yellow-50'}`}>
                <div className="flex">
                  {isRegistered ? (
                    <Check className="h-5 w-5 text-green-400 mr-2" />
                  ) : (
                    <X className="h-5 w-5 text-yellow-400 mr-2" />
                  )}
                  <p className={`text-sm ${isRegistered ? 'text-green-700' : 'text-yellow-700'}`}>
                    {isRegistered
                      ? `${selectedPatient.first_name} ${selectedPatient.last_name} is already registered for the mobile app.`
                      : `${selectedPatient.first_name} ${selectedPatient.last_name} is not yet registered for the mobile app.`}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {selectedPatient && qrCodeDataUrl && genericQrCodeDataUrl && (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">QR Codes for {selectedPatient.first_name} {selectedPatient.last_name}</h3>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Patient-specific QR Code */}
                <div className="flex flex-col items-center">
                  <div className="bg-gray-50 p-6 rounded-lg mb-4 w-full flex justify-center">
                    <img src={qrCodeDataUrl} alt="Patient Registration QR Code" className="max-w-full" />
                  </div>
                  <a 
                    href={`https://stirring-cactus-2d2f07.netlify.app/register?patientId=${selectedPatient.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:text-blue-800 hover:underline mb-2"
                  >
                    Open Registration Link
                  </a>
                  <h4 className="text-md font-semibold text-gray-900 mb-2">Patient-Specific Registration Link</h4>
                  <p className="text-sm text-gray-600 text-center mb-4">
                    This QR code contains a unique registration link for this patient.
                    <br />
                    <span className="font-medium">Use this for initial registration.</span>
                  </p>
                </div>

                {/* Generic QR Code */}
                <div className="flex flex-col items-center">
                  <div className="bg-gray-50 p-6 rounded-lg mb-4 w-full flex justify-center">
                    <img src={genericQrCodeDataUrl} alt="Generic App QR Code" className="max-w-full" />
                  </div>
                  <a 
                    href="https://stirring-cactus-2d2f07.netlify.app/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:text-blue-800 hover:underline mb-2"
                  >
                    Open App Homepage
                  </a>
                  <h4 className="text-md font-semibold text-gray-900 mb-2">General App Link</h4>
                  <p className="text-sm text-gray-600 text-center mb-4">
                    This QR code goes to the app homepage.
                    <br />
                    <span className="font-medium">Use this after registration is complete.</span>
                  </p>
                </div>
              </div>

              <div className="mt-6 border-t border-gray-200 pt-6">
                <h4 className="text-md font-semibold text-gray-900 mb-2">Instructions for Staff</h4>
                <ol className="list-decimal pl-5 text-sm text-gray-600 space-y-2">
                  <li>Have the patient scan the <strong>Patient-Specific Registration Link</strong> QR code with their phone camera.</li>
                  <li>Guide them through the registration process, which will require:
                    <ul className="list-disc pl-5 mt-1">
                      <li>Date of birth verification</li>
                      <li>Email address entry</li>
                      <li>Password creation</li>
                    </ul>
                  </li>
                  <li>After registration is complete, show them how to save the app to their home screen using the <strong>General App Link</strong> QR code.</li>
                  <li>Provide them with the printed instructions that include both QR codes.</li>
                </ol>
                
                <div className="mt-6">
                  <PatientQRCodePDF 
                    patientId={selectedPatient.patient_id}
                    patientName={`${selectedPatient.first_name} ${selectedPatient.last_name}`}
                    qrCodeUrl={`https://stirring-cactus-2d2f07.netlify.app/register?id=${selectedPatient.patient_id}`}
                    genericQrCodeUrl="https://stirring-cactus-2d2f07.netlify.app/"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}